import React, { Component } from 'react';

import './CollegeBlock.css';
import './JerseyM54.css';
import { parseSvgColors, applyColorSlots } from './parseSvgColors';

// Primary: the custom College Block OTF (loaded via CollegeBlock.css @font-face).
// Fallback chain covers environments where the webfont hasn't loaded yet.
const COLLEGIATE_FONT = "'CollegeBlock', 'Anton', 'Oswald', Impact, 'Arial Black', sans-serif";

// Jersey M54 font for names/words.
const JERSEY_FONT = "'JerseyM54', Arial, sans-serif";

/**
 * GarmentPreview
 *
 * Generic garment preview component. All assets and clip-path geometry are
 * received as props instead of being hard-coded.
 *
 * Props:
 *   baseImageUrl   – URL for the base garment PNG
 *   shaderImageUrl – URL for the multiply-blend shader PNG
 *   clipPaths      – [{ pathData: string, label: string }]  SVG clip path segments
 *   patterns       – [{ id, label, url }]  available patterns (for pre-fetch)
 *   options        – [{ id, label, url, colorSlots: [{ originalHex }] }]  available overlays
 *
 *   (All remaining props are identical to the old ShortsPreview API)
 *   patternId, patternColors, patternOriginals, onPatternColorsDiscovered
 *   stripeIds, stripeColors, stripePatterns, stripePatternColors, stripePatternOriginals
 *   onStripePatternColorsDiscovered
 *   logos, numbers, names
 */
// Parse all <path d="..."> strings from an SVG text string.
function parsePathsFromSvg(svgText) {
    const paths = [];
    const re = /<path[^>]+\bd="([^"]+)"/g;
    let m;
    while ((m = re.exec(svgText)) !== null) {
        paths.push(m[1]);
    }
    return paths;
}

class GarmentPreview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            patternSvgTexts: {},    // { patternId: rawSvgText }
            blobUrl: null,          // current object URL for the recolored pattern SVG
            stripeSvgTexts: {},     // { stripeId: rawSvgText }
            stripeParsedSlots: {},  // { stripeId: ColorSlot[] }  parsed from stripe SVG via parseSvgColors
            stripeBlobUrls: {},     // { stripeId: objectURL }  solid-colour stripe blobs
            stripeMaskUrls: {},     // { stripeId: objectURL }  white-fill mask blobs
            stripePatternBlobUrls: {}, // { stripeId: objectURL }  recoloured pattern for patterned stripes
            // Parsed from clipMaskUrl when provided — overrides clipPaths prop
            parsedClipPaths: null,  // string[] of path `d=` values, or null
        };
        this.svgRef = React.createRef();
    }

    componentDidMount() {
        const { patterns = [], options = [], clipMaskUrl } = this.props;

        // Fetch and parse the clip mask SVG if provided
        if (clipMaskUrl) {
            fetch(clipMaskUrl)
                .then((r) => r.text())
                .then((text) => {
                    const paths = parsePathsFromSvg(text);
                    if (paths.length > 0) {
                        this.setState({ parsedClipPaths: paths });
                    }
                })
                .catch((e) => console.warn('GarmentPreview: failed to load clipMaskUrl', e));
        }

        // Pre-fetch all pattern SVGs, parse their colors, notify parent
        patterns.forEach((p) => {
            fetch(p.url)
                .then((r) => r.text())
                .then((text) => {
                    const discovered = parseSvgColors(text);
                    if (this.props.onPatternColorsDiscovered) {
                        this.props.onPatternColorsDiscovered(p.id, discovered);
                    }
                    this.setState(
                        (prev) => ({
                            patternSvgTexts: { ...prev.patternSvgTexts, [p.id]: text },
                        }),
                        () => {
                            if (this.props.patternId === p.id) this.rebuildBlob();
                        }
                    );
                });
        });

        // Pre-fetch all option/stripe SVGs and parse their color slots
        options.forEach((s) => {
            if (!s.url) return;
            fetch(s.url)
                .then((r) => r.text())
                .then((text) => {
                    const slots = parseSvgColors(text);
                    this.setState(
                        (prev) => ({
                            stripeSvgTexts: { ...prev.stripeSvgTexts, [s.id]: text },
                            stripeParsedSlots: { ...prev.stripeParsedSlots, [s.id]: slots },
                        }),
                        () => {
                            if (this.props.stripeIds && this.props.stripeIds.includes(s.id)) {
                                this.rebuildAllStripeBlobs();
                            }
                        }
                    );
                });
        });
    }

    componentDidUpdate(prevProps) {
        // Re-fetch clip mask if URL changes
        if (prevProps.clipMaskUrl !== this.props.clipMaskUrl && this.props.clipMaskUrl) {
            fetch(this.props.clipMaskUrl)
                .then((r) => r.text())
                .then((text) => {
                    const paths = parsePathsFromSvg(text);
                    if (paths.length > 0) this.setState({ parsedClipPaths: paths });
                })
                .catch((e) => console.warn('GarmentPreview: failed to load clipMaskUrl', e));
        }

        // Re-fetch if the options/patterns prop set changes (e.g. on garment load)
        if (prevProps.options !== this.props.options || prevProps.patterns !== this.props.patterns) {
            this._prefetchNewAssets(prevProps);
        }

        if (
            prevProps.patternId !== this.props.patternId ||
            prevProps.patternColors !== this.props.patternColors
        ) {
            this.rebuildBlob();
        }
        if (
            prevProps.stripeIds !== this.props.stripeIds ||
            prevProps.stripeColors !== this.props.stripeColors ||
            prevProps.stripePatterns !== this.props.stripePatterns ||
            prevProps.stripePatternColors !== this.props.stripePatternColors ||
            prevProps.stripePatternOriginals !== this.props.stripePatternOriginals
        ) {
            this.rebuildAllStripeBlobs();
        }
    }

    _prefetchNewAssets(prevProps) {
        const prevPatternIds = new Set((prevProps.patterns || []).map((p) => p.id));
        const prevOptionIds  = new Set((prevProps.options || []).map((o) => o.id));

        (this.props.patterns || []).forEach((p) => {
            if (prevPatternIds.has(p.id)) return;
            fetch(p.url)
                .then((r) => r.text())
                .then((text) => {
                    const discovered = parseSvgColors(text);
                    if (this.props.onPatternColorsDiscovered) {
                        this.props.onPatternColorsDiscovered(p.id, discovered);
                    }
                    this.setState((prev) => ({
                        patternSvgTexts: { ...prev.patternSvgTexts, [p.id]: text },
                    }));
                });
        });

        (this.props.options || []).forEach((s) => {
            if (!s.url || prevOptionIds.has(s.id)) return;
            fetch(s.url)
                .then((r) => r.text())
                .then((text) => {
                    const slots = parseSvgColors(text);
                    this.setState(
                        (prev) => ({
                            stripeSvgTexts: { ...prev.stripeSvgTexts, [s.id]: text },
                            stripeParsedSlots: { ...prev.stripeParsedSlots, [s.id]: slots },
                        }),
                        () => {
                            if (this.props.stripeIds && this.props.stripeIds.includes(s.id)) {
                                this.rebuildAllStripeBlobs();
                            }
                        }
                    );
                });
        });
    }

    componentWillUnmount() {
        if (this.state.blobUrl) URL.revokeObjectURL(this.state.blobUrl);
        Object.values(this.state.stripeBlobUrls).forEach((u) => URL.revokeObjectURL(u));
        Object.values(this.state.stripeMaskUrls).forEach((u) => URL.revokeObjectURL(u));
        Object.values(this.state.stripePatternBlobUrls).forEach((u) => URL.revokeObjectURL(u));
    }

    rebuildBlob() {
        const { patternId, patternColors, patternOriginals } = this.props;
        const { patternSvgTexts } = this.state;

        const rawText = patternSvgTexts[patternId];
        const slots = patternOriginals[patternId];
        if (!rawText || !slots || slots.length === 0) return;

        if (this.state.blobUrl) URL.revokeObjectURL(this.state.blobUrl);

        const chosenColors = (patternColors && patternColors.length > 0)
            ? patternColors
            : slots.map((s) => s.color);
        const recolored = applyColorSlots(rawText, slots, chosenColors);

        const blob = new Blob([recolored], { type: 'image/svg+xml' });
        const blobUrl = URL.createObjectURL(blob);
        this.setState({ blobUrl });
    }

    rebuildAllStripeBlobs() {
        const {
            stripeIds, stripeColors,
            stripePatterns = {}, stripePatternColors = {}, stripePatternOriginals = {},
        } = this.props;
        const { stripeSvgTexts, stripeParsedSlots, stripeBlobUrls, stripeMaskUrls, stripePatternBlobUrls, patternSvgTexts } = this.state;

        Object.entries(stripeBlobUrls).forEach(([id, url]) => {
            if (!stripeIds.includes(id)) URL.revokeObjectURL(url);
        });
        Object.entries(stripeMaskUrls).forEach(([id, url]) => {
            if (!stripeIds.includes(id)) URL.revokeObjectURL(url);
        });
        Object.entries(stripePatternBlobUrls).forEach(([id, url]) => {
            if (!stripeIds.includes(id)) URL.revokeObjectURL(url);
        });

        const nextBlobUrls = {};
        const nextMaskUrls = {};
        const nextPatternBlobUrls = {};

        stripeIds.forEach((stripeId) => {
            const rawText = stripeSvgTexts[stripeId];
            if (!rawText) return;

            // Use parsed color slots from the SVG itself (handles named colors, hex, etc.)
            const slots = stripeParsedSlots[stripeId] || [];
            const chosenColors = stripeColors[stripeId] || [];

            const patternId = stripePatterns[stripeId] ?? null;

            if (patternId === null) {
                const recolored = applyColorSlots(rawText, slots, slots.map((s, i) => chosenColors[i] !== undefined ? chosenColors[i] : s.color));
                if (stripeBlobUrls[stripeId]) URL.revokeObjectURL(stripeBlobUrls[stripeId]);
                nextBlobUrls[stripeId] = URL.createObjectURL(new Blob([recolored], { type: 'image/svg+xml' }));
            } else {
                // White-fill mask: replace every slot color with white
                const whiteFill = applyColorSlots(rawText, slots, slots.map(() => '#ffffff'));
                if (stripeMaskUrls[stripeId]) URL.revokeObjectURL(stripeMaskUrls[stripeId]);
                nextMaskUrls[stripeId] = URL.createObjectURL(new Blob([whiteFill], { type: 'image/svg+xml' }));

                const patternRawText = patternSvgTexts[patternId];
                const patternSlots = (
                    stripePatternOriginals[stripeId] &&
                    stripePatternOriginals[stripeId][patternId]
                ) ? stripePatternOriginals[stripeId][patternId] : null;

                if (patternRawText && patternSlots) {
                    const chosenPatternColors = stripePatternColors[stripeId] || [];
                    let recolored = patternRawText;
                    patternSlots.forEach((slot, i) => {
                        const replacement = chosenPatternColors[i] !== undefined ? chosenPatternColors[i] : slot.color;
                        if (slot.type === 'replace') {
                            recolored = recolored.split(slot.value).join(replacement);
                        }
                    });
                    if (stripePatternBlobUrls[stripeId]) URL.revokeObjectURL(stripePatternBlobUrls[stripeId]);
                    nextPatternBlobUrls[stripeId] = URL.createObjectURL(new Blob([recolored], { type: 'image/svg+xml' }));
                }
            }
        });

        this.setState({
            stripeBlobUrls: nextBlobUrls,
            stripeMaskUrls: nextMaskUrls,
            stripePatternBlobUrls: nextPatternBlobUrls,
        });
    }

    render() {
        const { blobUrl, stripeBlobUrls, stripeMaskUrls, stripePatternBlobUrls } = this.state;
        const {
            stripeIds,
            stripePatterns = {},
            baseImageUrl,
            shaderImageUrl,
            imageTransform,
            clipPaths = [],
        } = this.props;

        // Use parsed clip paths from the uploaded SVG when available,
        // falling back to the stored path strings.
        const { parsedClipPaths } = this.state;
        const effectiveClipPaths = parsedClipPaths
            ? parsedClipPaths.map((d) => ({ pathData: d }))
            : clipPaths;

        // All assets are 1650×1550
        const W = 1650;
        const H = 1550;

        const imgStyle = imageTransform ? { transform: imageTransform } : undefined;

        return (
            <svg
                ref={this.svgRef}
                xmlns="http://www.w3.org/2000/svg"
                viewBox={`0 0 ${W} ${H}`}
                style={{ width: '100%', maxWidth: '57%', display: 'block', margin: '0 auto' }}
                aria-label="Garment preview"
            >
                <defs>
                    <clipPath id="garment-clip" clipPathUnits="userSpaceOnUse">
                        {effectiveClipPaths.map((cp, i) => (
                            <path key={i} d={cp.pathData} />
                        ))}
                    </clipPath>
                </defs>

                {/* Layer 1: base garment */}
                {baseImageUrl && (
                    <image href={baseImageUrl} x="0" y="0" width={W} height={H}
                        preserveAspectRatio="none" style={imgStyle} />
                )}

                {/* Layer 2: recolored pattern clipped to garment outline */}
                {blobUrl && (
                    <image href={blobUrl} x="0" y="0" width={W} height={H}
                        preserveAspectRatio="none"
                        clipPath="url(#garment-clip)" />
                )}

                {/* Layer 3: stripes — solid or pattern fill, clipped to garment */}
                {(stripeIds || []).map((id) => {
                    const patternId = stripePatterns[id] ?? null;

                    if (patternId === null) {
                        return stripeBlobUrls[id] ? (
                            <image key={id} href={stripeBlobUrls[id]} x="0" y="0" width={W} height={H}
                                preserveAspectRatio="none"
                                clipPath="url(#garment-clip)" />
                        ) : null;
                    }

                    const maskId = `stripe-mask-${id}`;
                    const maskUrl = stripeMaskUrls[id];
                    const patternUrl = stripePatternBlobUrls[id];
                    if (!maskUrl || !patternUrl) return null;

                    return (
                        <g key={id} clipPath="url(#garment-clip)">
                            <defs>
                                <mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse">
                                    <image href={maskUrl} x="0" y="0" width={W} height={H}
                                        preserveAspectRatio="none" />
                                </mask>
                            </defs>
                            <image href={patternUrl} x="0" y="0" width={W} height={H}
                                preserveAspectRatio="none"
                                mask={`url(#${maskId})`} />
                        </g>
                    );
                })}

                {/* Layer 4: logos — above stripes, clipped to garment, below multiply shader */}
                {(this.props.logos || []).map((logo) => {
                    const baseSize = 200;
                    const half = baseSize / 2;
                    return (
                        <g key={logo.id} clipPath="url(#garment-clip)">
                            <g transform={`translate(${logo.x},${logo.y}) rotate(${logo.rotate}) scale(${logo.scale})`}>
                                <image
                                    href={logo.dataUrl}
                                    x={-half}
                                    y={-half}
                                    width={baseSize}
                                    height={baseSize}
                                    preserveAspectRatio="xMidYMid meet"
                                />
                            </g>
                        </g>
                    );
                })}

                {/* Layer 5: numbers — collegiate block text, clipped to garment */}
                {(this.props.numbers || []).map((num) => {
                    const fontSize = 300 * num.scale;
                    return (
                        <g key={num.id} clipPath="url(#garment-clip)">
                            <text
                                x={num.x}
                                y={num.y}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontFamily={COLLEGIATE_FONT}
                                fontSize={fontSize}
                                fontWeight="900"
                                letterSpacing="8"
                                transform={`rotate(${num.rotate}, ${num.x}, ${num.y})`}
                                stroke={num.outlineColor}
                                strokeWidth={num.outlineWidth}
                                strokeLinejoin="miter"
                                strokeMiterlimit="10"
                                fill={num.fillColor}
                                paintOrder="stroke"
                                style={{ userSelect: 'none' }}
                            >
                                {num.value || '0'}
                            </text>
                        </g>
                    );
                })}

                {/* Layer 6: names — Jersey M54 text, clipped to garment */}
                {(this.props.names || []).map((name) => {
                    const fontSize = 180 * name.scale;
                    return (
                        <g key={name.id} clipPath="url(#garment-clip)">
                            <text
                                x={name.x}
                                y={name.y}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontFamily={JERSEY_FONT}
                                fontSize={fontSize}
                                transform={`rotate(${name.rotate}, ${name.x}, ${name.y})`}
                                stroke={name.outlineColor}
                                strokeWidth={name.outlineWidth}
                                strokeLinejoin="miter"
                                strokeMiterlimit="10"
                                fill={name.fillColor}
                                paintOrder="stroke"
                                style={{ userSelect: 'none' }}
                            >
                                {name.text}
                            </text>
                        </g>
                    );
                })}

                {/* Layer 7: shader with multiply blend */}
                {shaderImageUrl && (
                    <image href={shaderImageUrl} x="0" y="0" width={W} height={H}
                        preserveAspectRatio="none"
                        style={{ mixBlendMode: 'multiply', ...(imageTransform ? { transform: imageTransform } : {}) }} />
                )}
            </svg>
        );
    }
}

export default GarmentPreview;
